<script setup lang="ts">
import { computed } from 'vue';
import { OrderIntent, OrderType, TimeInForceType } from '@/modules/marketplace/types/marketplace';
import {
  orderIntentOptions,
  rateTypeOptions,
  timeInForceOptionsForOrder,
} from '@/modules/marketplace/helpers/marketplace';
import { AgreementInfo } from '@/modules/agreements/models';
import { AgreementStatus, RateType, Side } from '@/connect';
import PlainTimePicker from '@/modules/common/components/PlainTimePicker.vue';
import { TimeTuple } from '@/modules/common/models';
import { isTimeInTimeframe } from '@/modules/market-closed/helpers/market-closed';
import { setHours, setMinutes, setSeconds, subMinutes } from 'date-fns';
import MultipleTableAgreementSelector from '@/modules/agreements/components/MultipleTableAgreementSelector.vue';
import { store } from '@/store/store';
import { requiredIf, useRules } from '@/composables/vuetify-validation';
import { useCurrentTime } from '@/composables/current-time';

const emit = defineEmits<{
  (e: 'update:order-intent', value: OrderIntent): void;
  (e: 'update:time-in-force-type', value: TimeInForceType): void;
  (e: 'update:good-till-time', value: TimeTuple): void;
  (e: 'update:rate-type', value: RateType): void;
  (e: 'update:agreements', value: AgreementInfo[]): void;
  (e: 'update:allow-odd-lots', value: boolean): void;
}>();

const props = withDefaults(
  defineProps<{
    side?: Side;
    rateType?: RateType;
    orderType?: OrderType;
    orderIntent?: OrderIntent;
    agreements?: AgreementInfo[];
    timeInForceType?: TimeInForceType;
    goodTillTime?: TimeTuple;
    allowOddLots?: boolean;
  }>(),
  {
    side: Side.LOAN,
    rateType: RateType.FEE,
    orderType: 'LIMIT',
    orderIntent: 'FIRM',
    agreements: () => [],
    timeInForceType: 'DAY',
    goodTillTime: () => [12, 0],
    allowOddLots: false,
  }
);

const clientConfig = computed(() => store.state.clientConfig);
const { currentTimeUTC, localTimezone } = useCurrentTime();

const agreementStatuses = [AgreementStatus.ACTIVE];

const dependentTimeInForceOptions = computed(() =>
  timeInForceOptionsForOrder(props.orderType, props.orderIntent)
);

const selectedAgreements = computed({
  get: () => props.agreements,
  set: (value: AgreementInfo[]) => emit('update:agreements', value),
});

// @TODO: use once type "MARKET" is supported?
// const filteredOrderTypeOptions = computed(() =>
//   clientConfig.value.omsAllowMarketOrders
//     ? orderTypeOptions
//     : orderTypeOptions.filter((option) => option.value !== 'MARKET')
// );

function goodTillTimeToDate(now: Date, goodTillTime: TimeTuple): Date {
  return setSeconds(setMinutes(setHours(now, goodTillTime[0]), goodTillTime[1]), 0);
}

function inTheFuture(value: TimeTuple): true | string {
  return (
    props.timeInForceType !== 'GOOD_TILL_TIME' ||
    goodTillTimeToDate(currentTimeUTC.value, value) > currentTimeUTC.value ||
    'Must be in the future'
  );
}

function withinTimeFrame(value: TimeTuple): true | string {
  return (
    props.timeInForceType !== 'GOOD_TILL_TIME' ||
    isTimeInTimeframe(
      subMinutes(goodTillTimeToDate(currentTimeUTC.value, value), 1),
      clientConfig.value!.marketTimeframes.createLoans
    ) ||
    'Must be within market hours'
  );
}

const { rules } = useRules({
  rateType: {
    rules: [(v: RateType) => requiredIf(v, v !== RateType.UNSPECIFIED, 'Must be specified')],
  },
  goodTillTime: {
    rules: [
      (v: TimeTuple) =>
        requiredIf(v, props.timeInForceType !== 'GOOD_TILL_TIME', 'Must be specified'),
      (v: TimeTuple) => inTheFuture(v),
      (v: TimeTuple) => withinTimeFrame(v),
    ],
  },
});
</script>

<template>
  <VContainer>
    <VRadioGroup
      data-test="order-intent"
      label="Expose to venue as"
      row
      :value="orderIntent"
      @change="emit('update:order-intent', $event)"
    >
      <VRadio
        v-for="option in orderIntentOptions"
        :key="option.value"
        :data-test-id="`order-intent-${option.value}`"
        :label="option.text"
        :value="option.value"
      />
    </VRadioGroup>
    <div class="d-flex gap-4">
      <VSelect
        item-text="text"
        item-value="value"
        :items="dependentTimeInForceOptions"
        label="Time in Force"
        :value="timeInForceType"
        @change="emit('update:time-in-force-type', $event)"
      />
      <PlainTimePicker
        v-if="timeInForceType == 'GOOD_TILL_TIME'"
        class="ml-4"
        v-bind="rules['goodTillTime']"
        :label="'Good Till Time (' + localTimezone + ')'"
        :value="goodTillTime"
        @input="emit('update:good-till-time', $event)"
      />
    </div>
    <VSelect
      :items="rateTypeOptions"
      label="Rate Type"
      v-bind="rules['rateType']"
      :value="rateType"
      @input="emit('update:rate-type', $event)"
    />
    <VCheckbox
      class="mt-4"
      :input-value="allowOddLots"
      @change="emit('update:allow-odd-lots', $event)"
    >
      <template #label>
        <span>Allow Odd Lots</span>
        <VTooltip bottom open-delay="300">
          <template #activator="{ on, attrs }">
            <VIcon v-bind="attrs" class="grey--text ml-1" small v-on="on">
              mdi-information-outline
            </VIcon>
          </template>
          <div>
            When unchecked, quantity and min. quantity must be multiples of the lot size.
            <ul class="pl-4 mt-1">
              <li>Equities: 100</li>
              <li>Bonds: 1,000</li>
            </ul>
          </div>
        </VTooltip>
      </template>
    </VCheckbox>
    <MultipleTableAgreementSelector
      v-model="selectedAgreements"
      show-counterparty-breakdown
      :side="side"
      :statuses="agreementStatuses"
    />
  </VContainer>
</template>
